import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";


export default function BulletinNotificationBadge({ className = "" }) {
  const { user } = useAuth();
  const { activeOrganizationId, canUseOrganizationScope, scopeVersion } = useOrganizationScope();
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasCritical, setHasCritical] = useState(false);
  const navigate = useNavigate();

  const fetchCount = useCallback(async (signal) => {
    if (!user || !canUseOrganizationScope) return;

    try {
      const res = await api.get(`/bulletins/unread-count`, { signal });
      setUnreadCount(res.data.count || 0);
      setHasCritical(res.data.has_critical || false);
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        console.error("Failed to fetch bulletin count:", err);
      }
    }
  }, [canUseOrganizationScope, user]);

  useEffect(() => {
    const controller = new AbortController();

    setUnreadCount(0);
    setHasCritical(false);

    if (!user || !canUseOrganizationScope) {
      return () => controller.abort();
    }

    // Fetch when user is authenticated
    fetchCount(controller.signal);
    
    // Poll every 2 minutes
    const interval = setInterval(() => {
      if (user) {
        fetchCount(controller.signal);
      }
    }, 2 * 60 * 1000);

    // Listen for bulletin read events to refresh immediately
    const handleBulletinRead = () => {
      fetchCount(controller.signal);
    };
    window.addEventListener('bulletinRead', handleBulletinRead);
    
    return () => {
      controller.abort();
      clearInterval(interval);
      window.removeEventListener('bulletinRead', handleBulletinRead);
    };
  }, [activeOrganizationId, canUseOrganizationScope, user, fetchCount, scopeVersion]);

  return (
    <button
      onClick={() => navigate("/news")}
      className={`relative p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors ${className}`}
      data-testid="bulletin-notification-badge"
      title="News & Regulations"
    >
      <Bell className={`w-5 h-5 ${hasCritical ? "text-red-500" : "text-[var(--text-muted)]"}`} />
      
      {unreadCount > 0 && (
        <span
          className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white rounded-full px-1 ${
            hasCritical ? "bg-red-500 animate-pulse" : "bg-blue-500"
          }`}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
}
